"use client";

import { useEffect, useId, useState } from "react";
import FsLightbox from "fslightbox-react";
import SwiperType from "swiper";
import {
	A11y,
	FreeMode,
	Navigation,
	Scrollbar,
	Thumbs,
	Zoom,
} from "swiper/modules";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Image } from "@/lib/5874/types";
import { useProductContext } from "@/providers/ProductContextProvider";
import { useI18n } from "@/i18n/client";
import ZoomIn from "@/app/assets/images/svg/zoom-in.svg";
import IconArrow from "../UI/Icon/IconArrow/IconArrow";
import styles from "./ProductImageCarousel.module.scss";
import PlaceholderImage from "@/app/assets/images/img/placeholder-light.png";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/thumbs";
import CompressedImage from "../UI/CompressedImage/CompressedImage";
import SwiperNavigationButtons from "../SwiperNavigationButton/SwiperNavigationButton";

interface CarouselData {
	id: string;
	type: "image" | "video" | "youtube";
	src: string;
	thumbnail: string;
	altText?: string;
	shade?: string;
}

const ProductImageCarousel: React.FC = () => {
	const { product } = useProductContext();
	const carouselId = useId();
	const { media } = product;
	const t = useI18n();
	const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | undefined>();
	const [swiper, setSwiper] = useState<SwiperType | undefined>();
	const [toggler, setToggler] = useState<boolean>(false);
	const [activeSlide, setActiveSlide] = useState(0);
	const [carouselData, setCarouselData] = useState<CarouselData[]>([]);

	useEffect(() => {
		const getData = async () => {
			// const images = variant?.image
			// 	? [variant.image, ...product.images]
			// 	: [...product.images];

			const data = [] as CarouselData[];

			if (media.images) {
				// const shades = await Promise.all(
				// 	images.map((image) => {
				// 		try {
				// 			return getImageBrightness(image.url).then((res) => {
				// 				return res > 150 ? 'light' : 'dark';
				// 			});
				// 		} catch {
				// 			return 'light';
				// 		}
				// 	}),
				// );

				[...media.images].forEach((image: Image, index) => {
					const imageData = {
						id: String(Math.random()),
						type: "image",
						src: image.url.thumbnail,
						thumbnail: image.url.thumbnail,
						altText: product.name,
						// shade: shades[index],
					} as CarouselData;

					data.push(imageData);

					// if (variant?.image && image.url === variant.image.url) {
					// 	data.unshift(imageData);
					// } else {
					// 	if (!variant?.image && image.isDefault) {
					// 		data.unshift(imageData);
					// 	} else {
					// 		data.push(imageData);
					// 	}
					// }
				});
			}

			if (media.videos) {
				for (const video of media.videos) {
					data.push({
						id: String(video.id),
						type: video.type as any,
						src: `https://www.youtube.com/embed/${video.videoId}?v=${video.videoId}`,
						thumbnail: `https://i.ytimg.com/vi/${video.videoId}/maxresdefault.jpg`,
						shade: "dark",
					});
				}
			}

			setCarouselData(data);
			setActiveSlide(0);
		};

		getData();
	}, [media, product.name]);

	useEffect(() => {
		if (thumbsSwiper && thumbsSwiper.activeIndex !== activeSlide)
			thumbsSwiper.slideTo(activeSlide, 100, true);
		if (swiper && swiper.activeIndex !== activeSlide)
			swiper.slideTo(activeSlide, 100, true);
	}, [activeSlide, swiper, thumbsSwiper]);

	const getSlideContent = (data: CarouselData) => {
		switch (data.type) {
			case "image": {
				return (
					<CompressedImage
						src={data.src}
						altText={data?.altText || ""}
						loading={"eager"}
						fetchPriority={"high"}
						withNextImage={false}
					/>
				);
			}
			case "youtube": {
				return (
					<iframe
						title={data.src}
						src={data.src}
						allow='autoplay; fullscreen'
						allowFullScreen
					/>
				);
			}
		}
	};

	return (
		<div className={styles.productImageCarousel}>
			{/* <ProductBadge product={product} /> */}
			<Swiper
				onSwiper={setSwiper}
				className='carousel__swiper'
				modules={[Navigation, FreeMode, Scrollbar, A11y, Zoom]}
				zoom={{
					maxRatio: 3,
				}}
				spaceBetween={8}
				scrollbar={false}
				onSlideChange={(s) =>
					s.activeIndex !== activeSlide && setActiveSlide(s.activeIndex)
				}
			>
				{carouselData.length > 0 ? (
					carouselData.map((data, index) => (
						<SwiperSlide key={data.id} className='carousel__slides'>
							<div
								className={`${styles.imageWrapper} ${data.type} ${data.shade}`}
								onDoubleClick={() => setToggler(!toggler)}
								onKeyDown={(e) => e.key === "Enter" && setToggler(!toggler)}
								onMouseEnter={() => swiper?.zoom.in()}
								onMouseLeave={() => swiper?.zoom.out()}
							>
								<span className={styles.zoomIcon}>
									<ZoomIn />
								</span>
								<div className='swiper-zoom-container'>
									{getSlideContent(data)}
								</div>
							</div>
						</SwiperSlide>
					))
				) : (
					<SwiperSlide>
						<CompressedImage
							src={PlaceholderImage.src}
							altText='Movora placeholder image'
							withNextImage={false}
							fetchPriority={"high"}
						/>
					</SwiperSlide>
				)}
			</Swiper>
			{carouselData.length > 0 ? (
				<p className={styles.helperText}>{t("product.gallery.doubleClick")}</p>
			) : null}
			<Swiper
				className='carousel__swiper-thumbnails'
				onSwiper={setThumbsSwiper}
				onSlideChange={(s) =>
					s.activeIndex !== activeSlide && setActiveSlide(s.activeIndex)
				}
				centeredSlides={true}
				spaceBetween={8}
				slidesPerView={4}
				breakpoints={{
					551: {
						slidesPerView: "auto",
						spaceBetween: 10,
					},
				}}
				watchSlidesProgress={true}
				modules={[Navigation, FreeMode, Scrollbar]}
				navigation={{
					enabled: true,
					nextEl: `[data-button-ref="${carouselId}-button-next"]`,
					prevEl: `[data-button-ref="${carouselId}-button-previous"]`,
				}}
				scrollbar={false}
			>
				{carouselData?.map((data, index) => (
					<SwiperSlide
						onClick={() => setActiveSlide(index)}
						key={data.id}
						className='carousel__thumbnail'
					>
						<img src={data.thumbnail} alt={data.altText} />
					</SwiperSlide>
				))}
				<div className={styles.thumbnailBorder} />
				<SwiperNavigationButtons
					prevButtonClassName={`${styles.carouselArrowButtons} ${
						styles[`buttonPlacement--prev`]
					}`}
					nextButtonClassName={`${styles.carouselArrowButtons} ${
						styles[`buttonPlacement--next`]
					}`}
					buttonIdentifier={carouselId}
				/>
			</Swiper>
			<FsLightbox
				toggler={toggler}
				sources={
					carouselData.length ? carouselData.map((data) => data.src) : [""]
				}
				types={carouselData.map((data) => data.type)}
				thumbs={carouselData.map((data) => data.thumbnail)}
				showThumbsOnMount
				slide={activeSlide + 1}
				onClose={(instance) =>
					setActiveSlide((instance as any).stageIndexes.current)
				}
			/>
		</div>
	);
};

export default ProductImageCarousel;
